import { useRef } from 'react';
import { useDynamicList, useSubmit, useField, useReset, SubmitResult } from '@shopify/react-form';
import gettext from '@sdk/lang/gettext';
import validateEmail from '@sdk/utils/validation/validateEmail';
import useSlingApi from '@sdk/api/useSlingApi';
import { GENERIC_SERVER_ERROR } from '@sdk/login/constants';

import type {
  Employee,
  CurrentUser,
} from '@web/onboarding/Checklist/AddEmployeesModal/addEmployeesModal.types';
import type { AddPersonaCollectionUser } from '@web/onboarding/Checklist/onboardingChecklist.types';
import {
  emptyEmployeeFactory,
  prepareEmployeeForSave,
} from '@web/onboarding/Checklist/AddEmployeesModal/addEmployeesModal.utils';
import { getSubmitError } from '@web/forms/utils/getSubmitError';
import { FormSubmitStatuses } from '@web/forms/types';
import { decorateSubmit } from '@web/forms/utils/decorateSubmit';
import isValidName from '@web/onboarding/Checklist/AddEmployeesModal/validateName';
import { LAST_NAME_REQUIRED_TEXT } from '@web/onboarding/Checklist/AddEmployeesModal/constants';

export const useAddEmployeesForm = (
  defaultEmployees: Employee[],
  currentUser: CurrentUser,
  addPersonaCollectionUser: AddPersonaCollectionUser
) => {
  const api = useSlingApi();
  const {
    fields: employeesField,
    addItem,
    value,
  } = useDynamicList(
    {
      list: defaultEmployees,
      validates: {
        fullName: [
          (fullName, { siblings }) => {
            const fullNameTrimmed = fullName.trim();
            if (!fullNameTrimmed) {
              return;
            } else if (!isValidName(fullNameTrimmed)) {
              return gettext(LAST_NAME_REQUIRED_TEXT);
            }
          },
        ],
        email: [
          (email, { siblings }) => {
            const emailTrimmed = email.trim();
            if (!emailTrimmed) {
              // Email can be left out - returning 'undefined' here means validation passes
              return;
            }

            if (!validateEmail(emailTrimmed)) {
              return gettext('Provide a valid email address');
            }

            const isUsedForOtherEmployee = siblings.some(
              ({ email: siblingEmail }) => emailTrimmed === siblingEmail.value.trim()
            );
            if (emailTrimmed === currentUser.email || isUsedForOtherEmployee) {
              return gettext('This email address is already taken');
            }
          },
        ],
      },
    },
    emptyEmployeeFactory
  );
  const shouldSendInvite = useField(false);
  const formFields = {
    employees: employeesField,
    shouldSendInvite,
  };
  const reset = useReset(formFields);

  const submitPromise = useRef<Promise<SubmitResult>>();
  const {
    submit: formSubmit,
    errors,
    setErrors,
  } = useSubmit(async (fieldValues) => {
    const noEmployeeProvided = fieldValues.employees.every(({ fullName }) => !fullName.trim());

    if (noEmployeeProvided) {
      submitPromise.current = getSubmitError(gettext('Please provide at least one employee'));
      return submitPromise.current;
    }

    const employeesToSave = fieldValues.employees
      .map(prepareEmployeeForSave)
      .filter(({ name, lastname }) => name || lastname);

    if (!employeesToSave.length) {
      submitPromise.current = getSubmitError(gettext('Please provide at least one employee'));
      return submitPromise.current;
    }

    const employeeWithNoLastNameExists = fieldValues.employees
      .map(prepareEmployeeForSave)
      .filter(({ name, email }) => name && email)
      .some(({ lastname }) => !lastname);

    if (employeeWithNoLastNameExists) {
      submitPromise.current = getSubmitError(gettext(LAST_NAME_REQUIRED_TEXT));
      return submitPromise.current;
    }

    try {
      const {
        data: { users },
      } = await api.users.addNewUsers(fieldValues.shouldSendInvite, employeesToSave);
      users.forEach((createdUser) => addPersonaCollectionUser({ user: createdUser }));
    } catch (e) {
      submitPromise.current = getSubmitError(e.data?.message || GENERIC_SERVER_ERROR);
      return submitPromise.current;
    }

    submitPromise.current = Promise.resolve({ status: FormSubmitStatuses.success });
    return submitPromise.current;
  }, formFields);

  const submit = decorateSubmit(formSubmit, submitPromise);

  return {
    fields: formFields,
    addItem,
    value,
    submit,
    errors,
    setErrors,
    reset,
  };
};
